import * as Sentry from "@sentry/react"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "design-system/atoms/Accordion"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import { Separator } from "design-system/atoms/Separator"
import { Textarea } from "design-system/atoms/Textarea"
import { Card } from "design-system/molecules/Card"
import { MarkdownRenderer } from "design-system/organisms/chat/MarkdownRenderer"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { cn } from "design-system/utils"
import { renderLiquid } from "lopermut"
import { FlaskConical, List } from "lucide-react"
import { useTestFrontAppTicketMutation } from "modules/settings/hooks/frontapp/useTestFrontAppTicketMutation"
import { useEffect, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { WorkflowNodeSchema, CreateFrontAppTicketConfigSchema } from "schema"
import { toast } from "sonner"
import z from "zod"
import { useWorkflowBuilder } from "../../hooks/useWorkflowBuilder"
import { useEventPayload } from "../../hooks/useEventPayload"

type TicketPreviewProps = {
  formValues: z.infer<typeof CreateFrontAppTicketConfigSchema>
  eventPayload: Record<string, unknown>
}

function TicketPreview({ formValues, eventPayload }: TicketPreviewProps) {
  const { t } = useTranslation()

  const customerValues = {
    name: {
      copy: t("campaigns.action.availableActions.CreateFrontAppTicket.config.customer.name"),
      value: t(
        "campaigns.action.availableActions.CreateFrontAppTicket.config.customer.namePlaceholder",
      ),
    },
    email: {
      copy: t("campaigns.action.availableActions.CreateFrontAppTicket.config.customer.email"),
      value: t(
        "campaigns.action.availableActions.CreateFrontAppTicket.config.customer.emailPlaceholder",
      ),
    },
    phone: {
      copy: t("campaigns.action.availableActions.CreateFrontAppTicket.config.customer.phone"),
      value: t(
        "campaigns.action.availableActions.CreateFrontAppTicket.config.customer.phonePlaceholder",
      ),
    },
  }

  const values = {
    subject: {
      copy: t("campaigns.action.availableActions.CreateFrontAppTicket.config.subject"),
      value: formValues.subject,
    },
    body: {
      copy: t("campaigns.action.availableActions.CreateFrontAppTicket.config.body"),
      value: formValues.body,
    },
  }

  return (
    <Card className="flex flex-col gap-2">
      <Accordion type="single" collapsible className="w-full text-left">
        <AccordionItem value="preview">
          <AccordionTrigger>
            {t("campaigns.action.availableActions.CreateFrontAppTicket.config.preview")}
          </AccordionTrigger>
          <AccordionContent className="flex flex-col gap-3">
            <div className="flex flex-col gap-0">
              <span className="text-sm text-muted-foreground">
                {t("campaigns.action.availableActions.CreateFrontAppTicket.config.customer.title")}
              </span>
              {Object.entries(customerValues).map(([key, { copy, value }]) => (
                <div key={key} className="flex flex-row gap-0 items-center text-sm">
                  <span>{copy}</span>
                  <span>{":"}&nbsp;</span>
                  <span>{value}</span>
                </div>
              ))}
            </div>
            {Object.entries(values).map(([key, { copy, value }]) => {
              let displayedValue: string | number

              try {
                displayedValue = renderLiquid(value ? String(value) : "", eventPayload)
              } catch (error) {
                Sentry.captureException(error)
                displayedValue = ""
              }

              return (
                <div key={key} className="flex flex-col gap-0">
                  <span className="text-sm text-muted-foreground">{copy}</span>
                  <span className={cn("text-sm font-semibold")}>
                    {displayedValue && displayedValue.toString().length > 0 ? displayedValue : "-"}
                  </span>
                </div>
              )
            })}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  )
}

type CreateFrontAppTicketConfigurationProps = {
  nodeIndex: number
  node: Extract<z.infer<typeof WorkflowNodeSchema>, { type: "Action" }>
}

export function CreateFrontAppTicketConfiguration({
  nodeIndex,
  node,
}: CreateFrontAppTicketConfigurationProps) {
  const [testDialogOpen, setTestDialogOpen] = useState(false)

  const { t } = useTranslation()
  const { form } = useWorkflowBuilder()
  const { eventPayload, eventName } = useEventPayload({ nodeId: node.id })

  const { mutateAsync: testFrontAppTicket, isPending: isTestFrontAppTicketPending } =
    useTestFrontAppTicketMutation()

  async function handleTestFrontAppTicket() {
    const formConfig = form.getValues(`nodes.${nodeIndex}.config`)
    const result = CreateFrontAppTicketConfigSchema.safeParse(formConfig)

    if (!result.success) {
      toast.error(
        t("campaigns.action.availableActions.CreateFrontAppTicket.config.test.notCompletedFields"),
      )
      return
    }

    await testFrontAppTicket({
      data: formConfig as z.input<typeof CreateFrontAppTicketConfigSchema>,
      eventPayload: eventPayload ?? {},
    })

    toast.success(t("campaigns.action.availableActions.CreateFrontAppTicket.config.test.success"))
    setTestDialogOpen(false)
  }

  useEffect(() => {
    const result = CreateFrontAppTicketConfigSchema.safeParse(node.config)

    if (!result.success) {
      form.setValue(`nodes.${nodeIndex}.config`, {
        subject: "",
        body: "{{ data.summary }}",
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const subject = form.watch(`nodes.${nodeIndex}.config.subject`) || ""
  const body = form.watch(`nodes.${nodeIndex}.config.body`) || ""

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 justify-start items-start">
        <span className="text-sm text-muted-foreground">
          <Trans
            i18nKey="campaigns.action.availableActions.CreateFrontAppTicket.config.description"
            components={{
              b: <span className="font-medium" />,
              a: (
                <a
                  href="https://shopify.dev/docs/api/liquid"
                  target="_blank"
                  rel="noreferrer"
                  className="underline hover:opacity-80"
                />
              ),
            }}
          />
        </span>
      </div>
      <TicketPreview formValues={{ subject, body }} eventPayload={eventPayload ?? {}} />
      <div className="flex flex-row gap-2 justify-between w-full">
        <Button
          variant="testing"
          disabled={!form.formState.isValid}
          onClick={() => setTestDialogOpen(true)}
          className="flex-1"
        >
          <FlaskConical className="size-4" />
          <span>{t("campaigns.action.availableActions.CreateFrontAppTicket.config.test.cta")}</span>
        </Button>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="flex-1">
              <List className="size-4" />
              {t("campaigns.action.availableActions.CreateFrontAppTicket.config.data")}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="flex flex-col gap-2">
            <span className="text-base">
              <Trans
                i18nKey="campaigns.action.availableActions.CreateFrontAppTicket.config.availableData"
                components={{ b: <span className="font-medium" /> }}
                values={{ eventName }}
              />
            </span>
            <MarkdownRenderer
              className="bg-muted rounded-md overflow-auto max-h-96 border"
              codeClassName="border-none"
            >{`\`\`\`json\n${JSON.stringify(eventPayload, null, 2)}\n\`\`\``}</MarkdownRenderer>
          </PopoverContent>
        </Popover>
      </div>
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent>
          <DialogTitle>
            {t("campaigns.action.availableActions.CreateFrontAppTicket.config.test.title")}
          </DialogTitle>
          <DialogDescription>
            {t("campaigns.action.availableActions.CreateFrontAppTicket.config.test.warning")}
          </DialogDescription>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setTestDialogOpen(false)}
              disabled={isTestFrontAppTicketPending}
            >
              {t("shared.cancel")}
            </Button>
            <Button onClick={handleTestFrontAppTicket} disabled={isTestFrontAppTicketPending}>
              {t("shared.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Separator />
      <FormField
        control={form.control}
        name={`nodes.${nodeIndex}.config.subject`}
        render={() => (
          <FormItem>
            <FormLabel>
              {t("campaigns.action.availableActions.CreateFrontAppTicket.config.subject")}
              <span className="text-destructive">{" *"}</span>
            </FormLabel>
            <FormControl>
              <Input
                value={subject}
                onChange={(e) => {
                  form.setValue(`nodes.${nodeIndex}.config.subject`, e.target.value, {
                    shouldDirty: true,
                  })
                  form.trigger(`nodes.${nodeIndex}.config`)
                }}
                placeholder={t(
                  "campaigns.action.availableActions.CreateFrontAppTicket.config.subjectPlaceholder",
                )}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name={`nodes.${nodeIndex}.config.body`}
        render={() => (
          <FormItem>
            <FormLabel>
              {t("campaigns.action.availableActions.CreateFrontAppTicket.config.body")}
            </FormLabel>
            <FormControl>
              <Textarea
                value={body}
                onChange={(e) => {
                  form.setValue(`nodes.${nodeIndex}.config.body`, e.target.value, {
                    shouldDirty: true,
                  })
                  form.trigger(`nodes.${nodeIndex}.config`)
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.stopPropagation()
                  }
                }}
                placeholder={t(
                  "campaigns.action.availableActions.CreateFrontAppTicket.config.bodyPlaceholder",
                )}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  )
}
